import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Chip, TextField,
  Button, CircularProgress, Alert, AlertTitle, Avatar, Tooltip,
  IconButton, InputAdornment, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Paper,
  Autocomplete, Switch, FormControlLabel, Checkbox, Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AssignmentInd as AssignIcon, Add as AddIcon,
  Save as SaveIcon, Person as PersonIcon, Class as ClassIcon,
  Payments as IncomeIcon,
  CheckBox as CheckboxOnIcon, CheckBoxOutlineBlank as CheckboxOffIcon,
  RestartAlt as ResetIcon,
  TrendingUp as AddDiffIcon, TrendingDown as RemoveDiffIcon,
  CheckCircle as KeepDiffIcon,
  Edit as EditIcon, DeleteOutline as ClearIcon,
  Search as SearchIcon,
  Groups as PeopleIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const FONT_ACCT = `'IBM Plex Sans', 'Inter', sans-serif`;

// Sentinel used inside the "class_name" column when an assignment applies to
// every active student. The DB stores NULL for this; we round-trip it as this
// string only inside the picker's selected-array so multi-select works.
const ALL_CLASSES_TOKEN = '__ALL__';

export default function CollectorAssignmentsPage() {
  // Filter for the overview table below the form.
  const [overviewSearch, setOverviewSearch] = useState('');
  const [users, setUsers]             = useState([]);
  const [incomeTypes, setIncomeTypes] = useState([]);        // metadata (cat) for all types
  const [regSummary, setRegSummary]   = useState([]);        // [{income_type, students, classes:[{class_name, students}]}]
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading]         = useState(true);
  // The academic year every assignment on this page belongs to.
  // Collection routes used to be year-less, so one handed out two years
  // ago was still live; they are now scoped like teacher and register
  // assignments and this is the year they are written into.
  const [period, setPeriod]           = useState(null);

  // Form state
  const [userId, setUserId]               = useState('');
  const [pickedIncomes, setPickedIncomes] = useState([]); // [{trans, type, cat}, ...]
  const [allIncomes, setAllIncomes]       = useState(false);
  const [pickedClasses, setPickedClasses] = useState([]); // string class names
  const [allStudents, setAllStudents]     = useState(false);
  const [saving, setSaving]               = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [u, it, rs, a, p] = await Promise.all([
        API.get('/users'),
        API.get('/collections/income-types'),
        API.get('/daily-subscriptions/registered-summary'),
        API.get('/collections/assignments'),
        API.get('/collections/assignments/active-year'),
      ]);
      setUsers(u.data || []);
      setIncomeTypes(it.data || []);
      setRegSummary(rs.data || []);
      setAssignments(a.data || []);
      setPeriod(p.data || null);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load assignments'));
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const userOptions = useMemo(() => users.map(u => ({
    id: u.id,
    label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
    role: u.role_label || u.role_name || '',
    email: u.email,
  })), [users]);

  // Only income types that actually have registered students appear in the
  // picker. `cat` (category) comes from the /income-types metadata endpoint
  // so we can still group the dropdown by category.
  const incomeOptions = useMemo(() => {
    const meta = new Map(incomeTypes.map(t => [t.type, t]));
    return regSummary
      .map(r => {
        const m = meta.get(r.income_type);
        return {
          trans: m?.trans ?? r.income_type,
          type:  r.income_type,
          cat:   m?.cat || 'Other',
          students: r.students,
          classes:  r.classes,
        };
      })
      .sort((a, b) => (a.cat || '').localeCompare(b.cat || '') || a.type.localeCompare(b.type));
  }, [regSummary, incomeTypes]);

  // Classes shown depend on which income types are currently picked:
  //   - No income picked         → empty (user hasn't chosen scope yet)
  //   - "All types" toggle on    → every class with any registration
  //   - Specific incomes picked  → union of classes registered under them
  // Whatever classes the collector already owns are merged in so the
  // pre-populated selection always renders as chips (never "orphaned").
  const classOptions = useMemo(() => {
    const source = allIncomes ? incomeOptions : pickedIncomes;
    const set = new Set();
    for (const inc of source) for (const c of (inc.classes || [])) set.add(c.class_name);
    // Merge in classes from the current collector's existing assignments so
    // an already-assigned class that has since been un-registered still
    // shows up (and can be un-selected).
    if (userId) {
      for (const r of assignments) {
        if (Number(r.user_id) === Number(userId)
            && (r.assignment_kind || 'general') === 'general'
            && r.class_name) {
          set.add(r.class_name);
        }
      }
    }
    return Array.from(set).sort();
  }, [allIncomes, incomeOptions, pickedIncomes, userId, assignments]);

  // Only general-kind assignments live on this screen. Daily ones are
  // created/edited from Collection Registration and stay untouched.
  const existingForUser = useMemo(() => {
    if (!userId) return [];
    return assignments.filter(a =>
      Number(a.user_id) === Number(userId)
      && (a.assignment_kind || 'general') === 'general'
    );
  }, [assignments, userId]);

  // When a collector is picked, pre-populate the pickers with what they own
  // so the form doubles as an "edit" surface.
  useEffect(() => {
    if (!userId) {
      setPickedIncomes([]); setAllIncomes(false);
      setPickedClasses([]); setAllStudents(false);
      return;
    }
    const rows = assignments.filter(a =>
      Number(a.user_id) === Number(userId)
      && (a.assignment_kind || 'general') === 'general'
    );
    // Income types the collector currently owns
    const ownedTypes = Array.from(new Set(rows.map(r => r.income_type)));
    const ownedIncome = incomeOptions.filter(o => ownedTypes.includes(o.type));
    setPickedIncomes(ownedIncome);
    setAllIncomes(ownedIncome.length > 0 && ownedIncome.length === incomeOptions.length);
    // Classes: if any row has NULL class_name, the collector already covers
    // "all students" — flip the switch. Otherwise show the union of class names.
    const hasAll = rows.some(r => !r.class_name);
    if (hasAll) {
      setAllStudents(true);
      setPickedClasses([]);
    } else {
      setAllStudents(false);
      setPickedClasses(Array.from(new Set(rows.map(r => r.class_name).filter(Boolean))));
    }
  // We intentionally re-run whenever the underlying assignment list changes
  // too, so a Save that adds/removes rows immediately re-syncs the pickers.
  }, [userId, assignments, incomeOptions]);

  // ── Overview: who collects what, across the whole school ────────
  // The form above edits ONE collector at a time, which meant the only
  // way to learn who covers what was to select each person in turn.
  // This groups every current route by collector so the whole picture
  // is on screen, and doubles as the way in to editing one.
  const overview = useMemo(() => {
    const byUser = new Map();
    for (const a of assignments) {
      if ((a.assignment_kind || 'general') !== 'general') continue;
      const id = Number(a.user_id);
      if (!byUser.has(id)) {
        const u = userOptions.find(o => o.id === id);
        byUser.set(id, {
          user_id: id,
          label: u?.label || `User #${id}`,
          role: u?.role || '',
          email: u?.email || '',
          rows: [],
          types: new Set(),
        });
      }
      const g = byUser.get(id);
      g.rows.push(a);
      g.types.add(a.income_type);
    }
    const list = [...byUser.values()].map(g => ({
      ...g,
      typeList: [...g.types].sort(),
      // Classes per income type, so a route reads "Feeding · Nursery 1".
      byType: [...g.types].sort().map(t => ({
        income_type: t,
        classes: g.rows
          .filter(r => r.income_type === t)
          .map(r => r.class_name || 'All students')
          .sort(),
      })),
    }));
    const q = overviewSearch.trim().toLowerCase();
    return list
      .filter(g => !q
        || g.label.toLowerCase().includes(q)
        || g.email.toLowerCase().includes(q)
        || g.typeList.some(t => t.toLowerCase().includes(q))
        || g.byType.some(b => b.classes.some(c => c.toLowerCase().includes(q))))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [assignments, userOptions, overviewSearch]);

  // Remove every route a collector holds. Confirmed, because it silently
  // stops them being able to collect anything at all.
  const clearCollector = async (g) => {
    const ok = await swal.confirm({
      title: `Remove all routes for ${g.label}?`,
      text: `${g.rows.length} route${g.rows.length === 1 ? '' : 's'} will be removed. `
          + 'They will no longer see anything to collect. Registrations and past '
          + 'collections are untouched.',
      confirmText: 'Remove all',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await API.delete(`/collections/assignments/by-collector/${g.user_id}`);
      swal.success(`Routes removed for ${g.label}`);
      if (Number(userId) === g.user_id) setUserId('');
      await loadAll();
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not remove the routes'));
    }
  };

  // ── Desired vs existing sets, expressed as "income_type::class_name" ──
  // NULL class becomes the ALL_CLASSES_TOKEN sentinel so string keys work.
  const desiredKeys = useMemo(() => {
    const incomes = allIncomes ? incomeOptions : pickedIncomes;
    if (incomes.length === 0) return new Set();
    if (!allStudents && pickedClasses.length === 0) return new Set();
    const classes = allStudents ? [ALL_CLASSES_TOKEN] : pickedClasses;
    const set = new Set();
    for (const i of incomes) for (const c of classes) set.add(`${i.type}::${c}`);
    return set;
  }, [allIncomes, pickedIncomes, allStudents, pickedClasses, incomeOptions]);

  const existingIndex = useMemo(() => {
    // Map "income_type::class_name" → { id, ...row }
    const map = new Map();
    for (const r of existingForUser) {
      map.set(`${r.income_type}::${r.class_name || ALL_CLASSES_TOKEN}`, r);
    }
    return map;
  }, [existingForUser]);

  const toAdd    = useMemo(() => Array.from(desiredKeys).filter(k => !existingIndex.has(k)), [desiredKeys, existingIndex]);
  const toRemove = useMemo(() => Array.from(existingIndex.keys()).filter(k => !desiredKeys.has(k)), [desiredKeys, existingIndex]);
  const toKeep   = useMemo(() => Array.from(desiredKeys).filter(k => existingIndex.has(k)), [desiredKeys, existingIndex]);
  const hasChanges = toAdd.length > 0 || toRemove.length > 0;

  const reset = () => {
    setUserId('');
    setPickedIncomes([]); setAllIncomes(false);
    setPickedClasses([]); setAllStudents(false);
  };

  const save = async () => {
    if (!userId) return swal.error('Pick a collector first', 'Missing collector');
    if (!hasChanges) return swal.info('Nothing to save — no changes since load.');

    const removingEverything = toRemove.length > 0 && desiredKeys.size === 0;
    const ok = await swal.confirm({
      title: removingEverything ? 'Remove every assignment for this collector?' : 'Save changes?',
      text: [
        toAdd.length    ? `Add ${toAdd.length}`       : null,
        toRemove.length ? `Remove ${toRemove.length}` : null,
      ].filter(Boolean).join(' · '),
      tone: removingEverything ? 'danger' : 'success',
      confirmText: 'Yes, save',
    });
    if (!ok) return;

    setSaving(true);
    try {
      // Delete removed rows first so a re-add of the same (income, class)
      // pair doesn't collide with the UNIQUE(user_id, income, class, kind) key.
      for (const key of toRemove) {
        const row = existingIndex.get(key);
        if (row?.id) await API.delete(`/collections/assignments/${row.id}`);
      }
      if (toAdd.length > 0) {
        // Group new pairs by whether they're "all students" so we can send
        // one POST with the full cartesian (backend does income × class).
        const withAll     = toAdd.filter(k => k.endsWith(`::${ALL_CLASSES_TOKEN}`));
        const withClasses = toAdd.filter(k => !k.endsWith(`::${ALL_CLASSES_TOKEN}`));

        if (withAll.length > 0) {
          const incomes = Array.from(new Set(withAll.map(k => k.split('::')[0])));
          await API.post('/collections/assignments', {
            user_id: Number(userId),
            income_types: incomes,
            assignment_kind: 'general',
            class_names: null,
          });
        }
        if (withClasses.length > 0) {
          const incomes = Array.from(new Set(withClasses.map(k => k.split('::')[0])));
          const cls     = Array.from(new Set(withClasses.map(k => k.split('::')[1])));
          await API.post('/collections/assignments', {
            user_id: Number(userId),
            income_types: incomes,
            assignment_kind: 'general',
            class_names: cls,
          });
        }
      }
      swal.success(
        [
          toAdd.length    ? `Added ${toAdd.length}`      : null,
          toRemove.length ? `Removed ${toRemove.length}` : null,
        ].filter(Boolean).join(' · ') || 'Saved'
      );
      loadAll();
    } catch (e) {
      swal.error(swal.pickError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const selectedUser = userOptions.find(o => o.id === Number(userId)) || null;

  return (
    <Box sx={{ width: '100%', mx: 'auto', fontFamily: FONT_ACCT, px: 1 }}>
      <ModuleHeader
        icon={<AssignIcon />}
        title="Collector Assignments"
        subtitle={`Choose who collects what — by income type, by class, or any mix of both`
          + (period?.acc_yer ? ` · ${period.acc_yer}` : '')}
      />

      {/* Routes are stored against an academic year, so the page has to
          say which one it is writing into — and refuse to let anything
          be assigned when the school has not set one. */}
      {/* Period notices.
          Collections follow the school's ACCOUNTING calendar (Settings →
          Finance → Accounting Periods · Terms), not the examination one.
          Deliberately NO navigation button: the people who run
          collections have no access to Finance settings, and offering a
          button that lands them on a permission error is worse than
          telling them who to ask. */}
      {!loading && !period?.acc_yer && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          <AlertTitle sx={{ fontWeight: 800, mb: 0.2 }}>
            No academic year is set up
          </AlertTitle>
          Collection routes are stored against an academic year, so nothing can be
          assigned until one exists. An administrator sets the academic year and its
          terms under <strong>Settings → Finance → Accounting Periods · Terms</strong>.
        </Alert>
      )}

      {!loading && period?.acc_yer && period?.period_open === false && (
        <Alert severity="warning" sx={{ mb: 1.5 }}
          action={
            <Tooltip title="Re-check — use this once the period has been updated">
              <IconButton size="small" color="inherit" onClick={loadAll}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }>
          <AlertTitle sx={{ fontWeight: 800, mb: 0.2 }}>
            Today is outside the {period.acc_yer} accounting terms
          </AlertTitle>
          {period.block_reason
            || `Collections cannot be recorded today${period.end_dat
                  ? ` — the current term runs to ${String(period.end_dat).slice(0, 10)}` : ''}.`}
          {' '}Assigning collectors still works and is saved normally; they simply
          cannot open a day or take cash until a term covers today.
        </Alert>
      )}

      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{ px: 2.5, py: 1.4, borderBottom: 1, borderColor: 'divider',
          display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
          <AddIcon color="primary" />
          <Typography variant="subtitle2" fontWeight={800}>
            {selectedUser ? 'Edit Assignments' : 'New Assignment'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {selectedUser && existingForUser.length > 0 && (
            <Chip size="small" variant="outlined" color="primary"
              label={`${existingForUser.length} current`}
              sx={{ fontWeight: 700, mr: 0.5 }} />
          )}
          <Button size="small" color="inherit" onClick={reset} startIcon={<ResetIcon fontSize="small" />}>
            Reset
          </Button>
        </Box>

        <CardContent sx={{ pt: 2 }}>
          {loading && (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
          )}
          {!loading && (
            <>
              {/* Collector picker — full width so it clearly drives the form */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" fontWeight={800} color="text.secondary"
                  sx={{ letterSpacing: 1, display: 'block', mb: 0.5 }}>
                  1 · Collector
                </Typography>
                <Autocomplete fullWidth size="small" options={userOptions}
                  getOptionLabel={(o) => o.label || ''}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={selectedUser}
                  onChange={(_, v) => setUserId(v?.id || '')}
                  renderOption={(props, o) => (
                    <li {...props} key={o.id}>
                      <Box>
                        <Typography fontWeight={700}>{o.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {o.role || 'no role'} · {o.email}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(p) => (
                    <TextField {...p} label="Pick the staff member" required
                      helperText={
                        selectedUser
                          ? (existingForUser.length > 0
                              ? `${existingForUser.length} existing assignment${existingForUser.length === 1 ? '' : 's'} pre-loaded below — edit and save to apply changes`
                              : 'No current assignments — pick income type(s) and class(es) to create')
                          : 'Existing assignments will appear here when you pick someone'
                      }
                      InputProps={{ ...p.InputProps,
                        startAdornment: <PersonIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} /> }} />
                  )}
                />
              </Box>

              {/* Two-column grid: income types (left) · classes (right) */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 2.5, rowGap: 1.5,
              }}>
                {/* Income types */}
                <Box>
                  <Stack direction="row" alignItems="center" sx={{ mb: 0.3 }}>
                    <Typography variant="overline" fontWeight={800} color="text.secondary" sx={{ letterSpacing: 1 }}>
                      2 · Income Type
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <FormControlLabel
                      control={<Switch size="small" checked={allIncomes}
                        onChange={(e) => { setAllIncomes(e.target.checked); if (e.target.checked) setPickedIncomes([]); }} />}
                      label={<Typography variant="caption" fontWeight={700}>All types</Typography>}
                      sx={{ m: 0, height: 24 }}
                    />
                  </Stack>
                  {allIncomes ? (
                    <TextField fullWidth size="small" disabled
                      value={`All ${incomeOptions.length} registered income type${incomeOptions.length === 1 ? '' : 's'}`}
                      helperText="Only types with at least one registered student are included"
                      InputProps={{ startAdornment: <IncomeIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.disabled' }} /> }} />
                  ) : (
                    <Autocomplete multiple size="small" disableCloseOnSelect
                      options={incomeOptions}
                      value={pickedIncomes}
                      onChange={(_, v) => setPickedIncomes(v)}
                      getOptionLabel={(o) => o.type}
                      isOptionEqualToValue={(a, b) => a.trans === b.trans}
                      groupBy={(o) => o.cat}
                      noOptionsText="No income types have registered students yet — register some on Collection Registration first"
                      renderOption={(props, o, { selected }) => (
                        <li {...props} key={o.trans}>
                          <Checkbox size="small"
                            icon={<CheckboxOffIcon fontSize="small" />}
                            checkedIcon={<CheckboxOnIcon fontSize="small" />}
                            checked={selected} sx={{ mr: 0.5 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography fontSize={13} fontWeight={600} noWrap>{o.type}</Typography>
                          </Box>
                          <Chip size="small" variant="outlined"
                            label={`${o.students} student${o.students === 1 ? '' : 's'}`}
                            sx={{ fontSize: 10, height: 18, ml: 1 }} />
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((o, i) => <Chip key={o.trans} size="small" label={o.type} color="primary" variant="outlined" {...getTagProps({ index: i })} />)
                      }
                      renderInput={(p) => (
                        <TextField {...p} label="Pick income type(s)"
                          placeholder={pickedIncomes.length ? '' : 'Only types with registered students'}
                          helperText={
                            incomeOptions.length === 0
                              ? 'No income types have registered students yet'
                              : `${incomeOptions.length} registered type${incomeOptions.length === 1 ? '' : 's'} available`
                          }
                          InputProps={{ ...p.InputProps,
                            startAdornment: (
                              <>
                                <IncomeIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                                {p.InputProps.startAdornment}
                              </>
                            ) }} />
                      )}
                    />
                  )}
                </Box>

                {/* Classes */}
                <Box>
                  <Stack direction="row" alignItems="center" sx={{ mb: 0.3 }}>
                    <Typography variant="overline" fontWeight={800} color="text.secondary" sx={{ letterSpacing: 1 }}>
                      3 · Class
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <FormControlLabel
                      control={<Switch size="small" checked={allStudents}
                        onChange={(e) => { setAllStudents(e.target.checked); if (e.target.checked) setPickedClasses([]); }} />}
                      label={<Typography variant="caption" fontWeight={700}>All students</Typography>}
                      sx={{ m: 0, height: 24 }}
                    />
                  </Stack>
                  {allStudents ? (
                    <TextField fullWidth size="small" disabled value="All students registered under the picked income types"
                      helperText="The collector will see every registered student across selected income types"
                      InputProps={{ startAdornment: <ClassIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.disabled' }} /> }} />
                  ) : (
                    <>
                      <Autocomplete multiple size="small" disableCloseOnSelect
                        options={classOptions}
                        value={pickedClasses}
                        onChange={(_, v) => setPickedClasses(v)}
                        noOptionsText={
                          allIncomes || pickedIncomes.length > 0
                            ? 'No classes are registered under the selected income type(s)'
                            : 'Pick an income type first — classes are filtered by registration'
                        }
                        renderOption={(props, opt, { selected }) => (
                          <li {...props} key={opt}>
                            <Checkbox size="small"
                              icon={<CheckboxOffIcon fontSize="small" />}
                              checkedIcon={<CheckboxOnIcon fontSize="small" />}
                              checked={selected} sx={{ mr: 0.5 }} />
                            {opt}
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((opt, i) => <Chip key={opt} size="small" label={opt} color="secondary" variant="outlined" {...getTagProps({ index: i })} />)
                        }
                        renderInput={(p) => (
                          <TextField {...p} label="Pick class(es)"
                            placeholder={pickedClasses.length ? '' : 'Only classes with registrations'}
                            helperText={
                              !allIncomes && pickedIncomes.length === 0
                                ? 'Pick income type(s) first'
                                : classOptions.length === 0
                                  ? 'No classes registered under those income types'
                                  : `${classOptions.length} class${classOptions.length === 1 ? '' : 'es'} registered`
                            }
                            InputProps={{ ...p.InputProps,
                              startAdornment: (
                                <>
                                  <ClassIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                                  {p.InputProps.startAdornment}
                                </>
                              ) }} />
                        )}
                      />
                      {pickedClasses.length === 0 && classOptions.length > 0 && (
                        <Button size="small" sx={{ mt: 0.3, fontSize: 11 }} onClick={() => setPickedClasses(classOptions)}>
                          Quick-pick all {classOptions.length} classes
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </Box>

              {/* Diff summary — the "right space" you asked me to fill.
                  Sits full-width under the two pickers so both create and
                  edit flows see the same clear before/after picture. */}
              <DiffPanel
                userId={userId}
                toAdd={toAdd}
                toRemove={toRemove}
                toKeep={toKeep}
                desiredEmpty={desiredKeys.size === 0}
                existingEmpty={existingForUser.length === 0}
              />

              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                <Button onClick={reset} disabled={saving}>Reset</Button>
                <Button variant="contained" color="primary"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={save}
                  disabled={saving || !userId || !hasChanges}
                  sx={{ fontWeight: 700, minWidth: 180 }}>
                  {saving
                    ? 'Saving…'
                    : hasChanges
                      ? `Save${toAdd.length ? ` +${toAdd.length}` : ''}${toRemove.length ? ` −${toRemove.length}` : ''}`
                      : 'No changes'}
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Who collects what ───────────────────────────────────────
          The whole picture, so nobody has to click through every staff
          member to find out who covers Nursery 1's feeding. Clicking a
          row loads that collector into the form above. */}
      {!loading && (
        <Card sx={{ borderRadius: 2, mt: 2 }}>
          <Box sx={{
            px: { xs: 1.5, md: 2.5 }, py: 1.4, borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          }}>
            <PeopleIcon color="primary" />
            <Typography variant="subtitle2" fontWeight={800}>Who Collects What</Typography>
            <Chip size="small" variant="outlined"
              label={`${overview.length} collector${overview.length === 1 ? '' : 's'}`}
              sx={{ fontWeight: 700 }} />
            <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
            <TextField size="small" placeholder="Search collector, type or class"
              value={overviewSearch} onChange={(e) => setOverviewSearch(e.target.value)}
              InputProps={{ startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ) }}
              sx={{ width: { xs: '100%', sm: 280 } }} />
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, border: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }}>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11 }}>COLLECTOR</TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11 }}>ROUTES</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 800, fontSize: 11, width: 70 }}>TOTAL</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: 11, width: 96 }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      {assignments.length === 0
                        ? 'Nobody is assigned a collection route yet — use the form above.'
                        : 'No collector matches that search.'}
                    </TableCell>
                  </TableRow>
                )}
                {overview.map(g => (
                  <TableRow key={g.user_id} hover
                    selected={Number(userId) === g.user_id}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setUserId(g.user_id)}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{
                          width: 28, height: 28, fontSize: 12, fontWeight: 700,
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
                          color: 'primary.main',
                        }}>
                          {(g.label || '?')[0].toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 13 }} noWrap>
                            {g.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap
                            sx={{ display: 'block' }}>
                            {g.role || 'no role'}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.4}>
                        {g.byType.map(b => (
                          <Stack key={b.income_type} direction="row" spacing={0.5}
                            alignItems="center" flexWrap="wrap">
                            <Chip size="small" color="primary" label={b.income_type}
                              sx={{ height: 19, fontSize: 10.5, fontWeight: 800, color: '#fff' }} />
                            {b.classes.map((c, i) => (
                              <Chip key={`${c}-${i}`} size="small" variant="outlined" label={c}
                                sx={{ height: 19, fontSize: 10.5 }} />
                            ))}
                          </Stack>
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" variant="outlined" label={g.rows.length}
                        sx={{ height: 20, fontWeight: 800 }} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.3} justifyContent="flex-end">
                        <Tooltip title="Load into the form above to edit">
                          <IconButton size="small" color="primary"
                            onClick={(e) => { e.stopPropagation(); setUserId(g.user_id); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove all of their routes">
                          <IconButton size="small" color="error"
                            onClick={(e) => { e.stopPropagation(); clearCollector(g); }}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
}

// ── Diff panel ──────────────────────────────────────────────────────────
// Renders three chip groups (add / remove / keep). Encoded keys are
// "income_type::class_name-or-token" so we split once at the boundary.
function DiffPanel({ userId, toAdd, toRemove, toKeep, desiredEmpty, existingEmpty }) {
  if (!userId) {
    return (
      <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5, mt: 1.5, py: 0.4 }}>
        Pick a collector to see their current assignments and preview changes here.
      </Alert>
    );
  }
  if (desiredEmpty && existingEmpty) {
    return (
      <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1.5, mt: 1.5, py: 0.4 }}>
        Pick income type(s) and class(es) to create the first assignment for this collector.
      </Alert>
    );
  }

  const decode = (key) => {
    const idx = key.indexOf('::');
    const income = key.slice(0, idx);
    const cls    = key.slice(idx + 2);
    return { income, cls };
  };

  const renderChip = (key, kind) => {
    const { income, cls } = decode(key);
    const scope = cls === ALL_CLASSES_TOKEN ? 'All students' : cls;
    const color = kind === 'add' ? 'success' : kind === 'remove' ? 'error' : 'default';
    const sign  = kind === 'add' ? '+ ' : kind === 'remove' ? '− ' : '';
    return (
      <Chip
        key={`${kind}-${key}`}
        size="small"
        color={color}
        variant={kind === 'keep' ? 'outlined' : 'filled'}
        label={`${sign}${income} · ${scope}`}
        sx={{
          fontWeight: 700,
          height: 22,
          textDecoration: kind === 'remove' ? 'line-through' : 'none',
        }}
      />
    );
  };

  return (
    <Box sx={{
      mt: 1.5,
      p: 1.5,
      borderRadius: 1.5,
      border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.2)}`,
      bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="overline" fontWeight={800} color="text.secondary" sx={{ letterSpacing: 1 }}>
          Preview
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Chip size="small" icon={<AddDiffIcon sx={{ fontSize: 14 }} />} color="success"
          variant={toAdd.length ? 'filled' : 'outlined'}
          label={`${toAdd.length} to add`} sx={{ fontWeight: 700, height: 22 }} />
        <Chip size="small" icon={<RemoveDiffIcon sx={{ fontSize: 14 }} />} color="error"
          variant={toRemove.length ? 'filled' : 'outlined'}
          label={`${toRemove.length} to remove`} sx={{ fontWeight: 700, height: 22 }} />
        <Chip size="small" icon={<KeepDiffIcon sx={{ fontSize: 14 }} />} color="default"
          variant="outlined"
          label={`${toKeep.length} unchanged`} sx={{ fontWeight: 700, height: 22 }} />
      </Stack>

      {toAdd.length + toRemove.length + toKeep.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          Nothing selected yet.
        </Typography>
      ) : (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {toAdd.map(k => renderChip(k, 'add'))}
          {toRemove.map(k => renderChip(k, 'remove'))}
          {toKeep.length > 0 && toAdd.length + toRemove.length > 0 && (
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          )}
          {toKeep.map(k => renderChip(k, 'keep'))}
        </Stack>
      )}
    </Box>
  );
}
